import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
} from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { OnboardingGate } from './OnboardingGate';
import { AdminShell } from '@/components/layout/AdminShell';
import { PublicShell } from '@/components/layout/PublicShell';

import { LoginPage } from '@/features/auth/LoginPage';
import { RegisterPage } from '@/features/auth/RegisterPage';
import { OtpPage } from '@/features/auth/OtpPage';
import { ForgotPasswordPage } from '@/features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from '@/features/auth/ResetPasswordPage';
import { OAuthCallbackPage } from '@/features/auth/OAuthCallbackPage';
import { PendingAccessPage } from '@/features/auth/PendingAccessPage';
import { BillingSuccessPage as StripeBillingSuccessPage } from '@/features/billing/BillingSuccessPage';
import { BrandProfilePage } from '@/features/onboarding/BrandProfilePage';
import { PricingPage } from '@/features/billing/PricingPage';
import { PricingEmbedPage } from '@/features/billing/PricingEmbedPage';
import { BillingPage } from '@/features/billing/BillingPage';
import { CheckoutSuccessPage, CheckoutCancelPage } from '@/features/billing/CheckoutResult';
import { DashboardPage } from '@/features/dashboard/DashboardPage';
import { AgencyProfilePage } from '@/features/agency/AgencyProfilePage';
import { BrandsGalleryPage } from '@/features/brands/BrandsGalleryPage';
import { BrandProfilePage as BrandHomePage } from '@/features/brand/BrandProfilePage';
import { BrandLeftoversPage } from '@/features/brand/BrandLeftoversPage';
import { BrandXotskiesPage } from '@/features/brand/BrandXotskiesPage';
import { BrandProjectsPage } from '@/features/brand/BrandProjectsPage';
import { ProjectDetailPage } from '@/features/project/ProjectDetailPage';
import { EventDetailPage } from '@/features/events/EventDetailPage';
import { CalendarPage } from '@/features/calendar/CalendarPage';
import { StationListPage } from '@/features/stations/StationListPage';
import { StationDetailPage } from '@/features/stations/StationDetailPage';
import { StockListPage } from '@/features/stock/StockListPage';
import { BrandInventoryListPage } from '@/features/brand-inventory/BrandInventoryListPage';
import { MerchPlannerPage } from '@/features/merch-planner/MerchPlannerPage';
import { BrandSettingsPage } from '@/features/settings/BrandSettingsPage';
import { WorkspaceSettingsPage } from '@/features/settings/WorkspaceSettingsPage';
import { LegalSettingsPage } from '@/features/settings/LegalSettingsPage';
import { TagManagerPage } from '@/features/tags/TagManagerPage';
import { SessionsPage } from '@/features/settings/SessionsPage';
import { BrandIdentityPage } from '@/features/brand-identity/BrandIdentityPage';
import { BrandStudioNewPage } from '@/features/brand-studio/BrandStudioNewPage';
import { ContactsPage } from '@/features/contacts/ContactsPage';
import { ContactDetailPage } from '@/features/contacts/ContactDetailPage';
import { InsightsPage } from '@/features/insights/InsightsPage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { AccessManagerPage } from '@/features/access-manager/AccessManagerPage';
import { ActiveLogPage } from '@/features/active-log/ActiveLogPage';
import { ProfilePage } from '@/features/profile/ProfilePage';
import { ComposeNotificationPage } from '@/features/notifications/ComposeNotificationPage';
import { PublicSurveyPage } from '@/features/public-survey/PublicSurveyPage';
import { FinishPage } from '@/features/public-survey/FinishPage';
import { ShortLinkRedirect } from '@/features/public-survey/ShortLinkRedirect';
import { SharedQrLanding } from '@/features/public-survey/SharedQrLanding';
// Caso 02 — Event report (admin + public share link).
import { PublicEventReportPage } from '@/features/public-report/PublicEventReportPage';
// CH-03 — página pública de marca, cerrada al equipo hasta que se abra.
import { PublicBrandPage } from '@/features/public-brand/PublicBrandPage';
// PRD §12 — Reporter View (magic-link, 24h, no account).
import { ReporterView } from '@/features/reporter/ReporterView';
// Caso 03 — Prize redemption (operator-facing).
import { RedeemPrizePage } from '@/features/redemption/RedeemPrizePage';
// Caso 04 — Post-event follow-up settings.
import { FollowUpSettingsPage } from '@/features/followup/FollowUpSettingsPage';
// Caso 13 — Outbound CRM integrations (webhook ships; OAuth providers stubbed).
import { IntegrationsPage } from '@/features/integrations/IntegrationsPage';
// Caso 12 parte 2 — Event-to-event trend comparison.
import { EventComparisonPage } from '@/features/trends/EventComparisonPage';
// Caso 11 — Booth-operator station view.
import { OperatorStationPage } from '@/features/operator/OperatorStationPage';
// Caso 11 v4 — Public entry that exchanges a station-token URL for a
// short-lived operator session and routes into the operator view.
import { OperatorLoginPage } from '@/features/operator/OperatorLoginPage';
// In-app Help Center — bilingual, searchable, screen-by-screen.
import { HelpPage } from '@/features/help/HelpPage';

const router = createBrowserRouter([
  // Public — no auth
  {
    element: <PublicShell />,
    children: [
      { path: '/login', element: <LoginPage /> },
      { path: '/register', element: <RegisterPage /> },
      { path: '/auth/otp', element: <OtpPage /> },
      { path: '/auth/forgot-password', element: <ForgotPasswordPage /> },
      { path: '/auth/reset-password', element: <ResetPasswordPage /> },
      { path: '/auth/oauth-callback', element: <OAuthCallbackPage /> },
      { path: '/pricing', element: <PricingPage /> },
      { path: '/s/:surveyId/:stationId', element: <ShortLinkRedirect /> },
      // Shared QR — one code, several stations. Lands on a station picker.
      { path: '/sg/:groupId', element: <SharedQrLanding /> },
      { path: '/public-surveys', element: <PublicSurveyPage /> },
      { path: '/public-finish', element: <FinishPage /> },
      // Caso 02 — public share link for event-result reports.
      { path: '/r/:token', element: <PublicEventReportPage /> },
      { path: '/b/:slug', element: <PublicBrandPage /> },
      // El bloque de planes y calculadora que xotski.com enmarca: sin cabecera ni pie.
      { path: '/embed/pricing', element: <PricingEmbedPage /> },
      // PRD §12 — Reporter View: 24h magic-link, no account, scoped to one
      // event. Renders its own full-screen layout (header + tab nav).
      { path: '/reporter/:token', element: <ReporterView /> },
      // Caso 11 v4 — operator login: trades the URL token for a session.
      { path: '/operator-login', element: <OperatorLoginPage /> },
    ],
  },

  // Authed (protected)
  {
    element: <ProtectedRoute />,
    children: [
      // Onboarding routes — gate enforces (no shell, dedicated layout)
      {
        element: <OnboardingGate />,
        children: [
          {
            path: '/onboarding/brand-profile',
            element: <BrandProfilePage />,
          },
          { path: '/pending-access', element: <PendingAccessPage /> },

          // Admin shell — gate redirects out if onboarding incomplete
          {
            element: <AdminShell />,
            children: [
              { index: true, element: <DashboardPage /> },
              // In-app Help Center — reached from the `?` button in the TopBar
              // and from contextual `<HelpHint section="…" />` deep-links.
              { path: 'help', element: <HelpPage /> },
              // Galería de marcas: desde el selector del topbar («Todas las marcas»).
              { path: 'brands', element: <BrandsGalleryPage /> },
              { path: 'agency/:id', element: <AgencyProfilePage /> },
              { path: 'brand/:id', element: <BrandHomePage /> },
              { path: 'brand/:id/projects', element: <BrandProjectsPage /> },
              { path: 'brand/:id/xotskies', element: <BrandXotskiesPage /> },
              { path: 'brand/:id/leftovers', element: <BrandLeftoversPage /> },
              { path: 'brand-identity', element: <BrandIdentityPage /> },
              { path: 'brand-studio', element: <Navigate to="/brand-identity" replace /> },
              { path: 'brand-studio/new', element: <BrandStudioNewPage /> },
              { path: 'brand-studio/:id', element: <BrandStudioNewPage /> },

              { path: 'projects/:id', element: <ProjectDetailPage /> },
              { path: 'projects/:id/events/:eventId', element: <EventDetailPage /> },
              { path: 'calendar', element: <CalendarPage /> },
              // The event's report and live monitor are tabs on the event
              // itself now (tasks 5.2 / 5.3), not routes of their own. The
              // public share link at /r/:token is unaffected.
              // Caso 03 — booth operator's redemption flow.
              { path: 'redeem', element: <RedeemPrizePage /> },
              // Caso 04 — Post-event follow-up settings.
              { path: 'settings/followup', element: <FollowUpSettingsPage /> },
              // Caso 13 — Outbound CRM integrations (webhook fully working,
              // OAuth providers are listed but disabled until apps registered).
              { path: 'settings/integrations', element: <IntegrationsPage /> },
              // Caso 11 — Booth-operator's mobile-first station view.
              { path: 'operator/:id', element: <OperatorStationPage /> },
              // The live monitor is the event's Track tab now (task 5.2) and
              // the report is its Report tab (5.3) — neither is a page you
              // navigate away to, so both routes are gone.

              { path: 'stations', element: <StationListPage /> },
              { path: 'stations/:id', element: <StationDetailPage /> },
              // The standalone QR page is gone — the QR opens in context
              // (StationQrDialog) from the station, the list and operator mode.

              { path: 'stock', element: <StockListPage /> },
              { path: 'brand-inventory', element: <BrandInventoryListPage /> },
              { path: 'merch-planner', element: <MerchPlannerPage /> },
              // Alias: la calculadora pasó a Merch planner (favoritos y enlaces antiguos).
              { path: 'calculator', element: <Navigate to="/merch-planner" replace /> },

              // Task 4.1 — the template library is gone; a journey starts from
              // one starting point applied in an event's Design Journey tab.
              // Old links land on the home rather than 404ing. (Not `/surveys`:
              // there is no journey index route, so that would fall through the
              // catch-all to the same place by accident instead of on purpose.)
              { path: 'surveys/templates', element: <Navigate to="/" replace /> },
              // The read-only journey-steps page is gone: the game name now
              // opens the live consumer preview, and editing opens Design Journey.
              // El builder de encuestas se retiró: las encuestas se diseñan en
              // Xurvey, que es su aplicación, y Xotski las despliega (protocolo
              // de integración §3 — una app no edita la encuesta que usa).
              // Las rutas siguen aquí para que un enlace guardado aterrice en
              // algún sitio en vez de dar 404.
              { path: 'surveys/builder', element: <Navigate to="/" replace /> },
              { path: 'surveys/builder/:id', element: <Navigate to="/" replace /> },
              // Caso 12 parte 2 — Event trend comparison. Routes nest under
              // project / brand for natural deep-linking; a flat /trends
              // route uses query strings for external embeds.
              { path: 'projects/:id/trends', element: <EventComparisonPage /> },
              { path: 'brand/:id/trends', element: <EventComparisonPage /> },
              { path: 'trends', element: <EventComparisonPage /> },

              { path: 'contacts', element: <ContactsPage /> },
              { path: 'contacts/:id', element: <ContactDetailPage /> },
              { path: 'insights', element: <InsightsPage /> },

              { path: 'settings', element: <SettingsPage /> },
              { path: 'settings/workspace', element: <WorkspaceSettingsPage /> },
              { path: 'settings/brand', element: <BrandSettingsPage /> },
              { path: 'settings/legal', element: <LegalSettingsPage /> },
              { path: 'settings/tags', element: <TagManagerPage /> },
              { path: 'settings/sessions', element: <SessionsPage /> },
              { path: 'settings/billing', element: <BillingPage /> },
              { path: 'settings/access-manager', element: <AccessManagerPage /> },
              { path: 'active-log', element: <ActiveLogPage /> },
              { path: 'profile', element: <ProfilePage /> },

              { path: 'notifications/compose', element: <ComposeNotificationPage /> },

              { path: 'billing/success', element: <CheckoutSuccessPage /> },
              { path: 'billing/cancel', element: <CheckoutCancelPage /> },
              // Stripe-backed return URL (production).
              { path: 'billing/stripe-success', element: <StripeBillingSuccessPage /> },
            ],
          },
        ],
      },
    ],
  },

  // Catch-all
  { path: '*', element: <Navigate to="/" replace /> },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
